"use client";

import { useState } from "react";
import { Star, BadgeCheck } from "lucide-react";

export type QuoteItem = {
  quote: string;
  name: string;
  role: string;
  initials: string;
  /** Optional avatar image. Falls back to initials. */
  avatar?: string;
};

/**
 * Premium full-width testimonial carousel.
 * Renders a single card with prev/next dot navigation cycling through items.
 * Background image inside the card at 15% opacity, lime left-border, two-column layout.
 */
export function InlineQuoteCarousel({
  items,
  bgImage = "/images/testimonials/quote-bg.jpg",
}: {
  items: QuoteItem[];
  bgImage?: string;
}) {
  const [active, setActive] = useState(0);
  const t = items[active];

  return (
    <section
      className="relative overflow-hidden border-b border-border"
      style={{ backgroundColor: "#0D0D0D" }}
    >
      {/* Radial lime glow behind card */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 70% at center, rgba(191,247,69,0.05) 0%, rgba(191,247,69,0) 70%)",
        }}
      />

      <div className="relative site-container py-16 lg:py-20">
        <figure
          key={active}
          className="quote-card relative isolate overflow-hidden rounded-xl"
          style={{
            backgroundColor: "#111111",
            border: "1px solid #1E1E1E",
            borderLeft: "4px solid #bff745",
            animation: "quote-fade-in 400ms ease-out",
          }}
        >
          {/* Background image */}
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-20 bg-cover bg-center"
            style={{
              backgroundImage: `url(${bgImage})`,
              opacity: 0.4,
            }}
          />
          {/* Soft inner darkening so text reads */}
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10"
            style={{
              background:
                "linear-gradient(135deg, rgba(17,17,17,0.7) 0%, rgba(17,17,17,0.3) 60%, rgba(17,17,17,0.65) 100%)",
            }}
          />

          {/* Big decorative quote mark */}
          <span
            aria-hidden="true"
            className="absolute -top-6 left-6 lg:left-10 select-none font-extrabold leading-none"
            style={{
              fontSize: "8rem",
              color: "rgba(191,247,69,0.4)",
              letterSpacing: "-0.05em",
            }}
          >
            &ldquo;
          </span>

          <div className="relative grid gap-8 lg:grid-cols-12 lg:gap-10 p-7 lg:p-10 pt-14 lg:pt-16">
            {/* Left column — quote + stars */}
            <div className="lg:col-span-8">
              <div className="flex items-center gap-1 mb-5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="size-5 fill-accent text-accent"
                    aria-hidden="true"
                    style={{
                      filter: "drop-shadow(0 0 6px rgba(191,247,69,0.55))",
                    }}
                  />
                ))}
              </div>

              <blockquote
                className="text-text font-semibold leading-tight"
                style={{ fontSize: "clamp(1.25rem, 1.2vw + 0.85rem, 1.75rem)" }}
              >
                &ldquo;{t.quote}&rdquo;
              </blockquote>
            </div>

            {/* Right column — avatar + name + brand */}
            <div className="lg:col-span-4 flex flex-col items-start lg:items-end justify-between gap-6">
              <span
                aria-hidden="true"
                className="relative inline-flex size-20 items-center justify-center rounded-full text-h3 font-bold text-accent shrink-0"
                style={{
                  backgroundColor: "#0a0a0a",
                  boxShadow:
                    "0 0 0 3px rgba(191,247,69,0.55), 0 0 32px rgba(191,247,69,0.25)",
                }}
              >
                {t.avatar ? (
                  <span
                    aria-hidden="true"
                    className="absolute inset-1 rounded-full bg-cover bg-center"
                    style={{ backgroundImage: `url(${t.avatar})` }}
                  />
                ) : (
                  t.initials
                )}
              </span>

              <div className="text-left lg:text-right">
                <div className="inline-flex items-center gap-1.5 text-body font-semibold text-text">
                  {t.name}
                  <BadgeCheck
                    className="size-4 text-accent"
                    aria-hidden="true"
                    style={{
                      filter: "drop-shadow(0 0 6px rgba(191,247,69,0.55))",
                    }}
                  />
                </div>
                <div className="text-small text-text-muted mt-1">{t.role}</div>
              </div>
            </div>
          </div>
        </figure>

        {/* Dot navigation */}
        {items.length > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2.5">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`Show testimonial ${i + 1}`}
                aria-current={i === active}
                className="group relative inline-flex items-center justify-center size-6 rounded-full transition-colors"
              >
                <span
                  className="inline-block rounded-full transition-all duration-300"
                  style={{
                    width: i === active ? 26 : 8,
                    height: 8,
                    backgroundColor:
                      i === active ? "#bff745" : "rgba(255,255,255,0.25)",
                    boxShadow:
                      i === active
                        ? "0 0 12px rgba(191,247,69,0.6)"
                        : "none",
                  }}
                />
              </button>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes quote-fade-in {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}
